import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import { createReport, updateReport } from '../../services/report/reportService';
import { useNavigate } from 'react-router-dom';
import { FileMetadata } from '../../stores/appReportsStore';
import { SourcingData } from '../../stores/appReportsStore';
import { useAppUserDefaultStore } from '../../stores/appUserDefaultStore';
import { useRestaurantStore } from '../../stores/appRestaurantStore';

interface SaveReportModalProps {
  isOpen: boolean;
  onClose: () => void;
  fileMetadata: FileMetadata;
  sourcingData: SourcingData;
  mode: 'create' | 'update';
  reportId?: string;
  restaurantId?: string;
}

export default function SaveReportModal({
  isOpen,
  onClose,
  fileMetadata,
  sourcingData,
  mode,
  reportId,
  restaurantId,
}: SaveReportModalProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const today = new Date().toISOString().split('T')[0];

  const restaurants = useRestaurantStore(s => s.restaurants);
  const [selectedRestaurantId, setSelectedRestaurantId] = useState<string>("");
  const restaurant = useAppUserDefaultStore(s => s.restaurant)

  const parseDateForInput = (dateStr: string): string => {
    const isoMatch = dateStr.match(/^(\d{4}-\d{2}-\d{2})/);
    if (isoMatch) {
      return isoMatch[1];
    }

    // dd-mm-yy (e.g., "12-05-24")
    if (/^\d{2}-\d{2}-\d{2}$/.test(dateStr)) {
      const [dd, mm, yy] = dateStr.split('-');
      const fullYear = `20${yy}`;
      return `${fullYear}-${mm}-${dd}`;
    }
    // dd-mm-yyyy (e.g., "12-05-2024")
    if (/^\d{2}-\d{2}-\d{4}$/.test(dateStr)) {
      const [dd, mm, yyyy] = dateStr.split('-');
      return `${yyyy}-${mm}-${dd}`;
    }
    return '';
  };

  // Initialize name/start/end in ISO format for the date inputs
  const initialName = fileMetadata.fileName || '';
  const initialStartIso = parseDateForInput(fileMetadata.startDate || '');
  const initialEndIso = parseDateForInput(fileMetadata.endDate || '');

  const [name, setName] = useState<string>(initialName);
  const [startDate, setStartDate] = useState<string>(initialStartIso || today);
  const [endDate, setEndDate] = useState<string>(initialEndIso || today);
  const [saving, setSaving] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // If modal re‐opens, reset state
  useEffect(() => {
    if (isOpen) {
      setName(initialName);
      setStartDate(initialStartIso || today);
      setEndDate(initialEndIso || today);
      setError(null);
    }
  }, [isOpen, initialName, initialStartIso, initialEndIso, today]);

  useEffect(() => {
    if (!isOpen) return;
    if (mode === 'update' && restaurantId) {
      setSelectedRestaurantId(restaurantId);
    } else if (restaurants.length > 0) {
      setSelectedRestaurantId(restaurant?.id || restaurants[0].id);
    }
  }, [isOpen, mode, restaurantId, restaurant, restaurants]);

  const handleSave = async () => {
    if (!name.trim()) {
      setError(t('reports.saveModal.errorName') || 'Name is required');
      return;
    }
    if (!startDate || !endDate) {
      setError(t('reports.saveModal.errorDates') || 'Both start and end dates are required');
      return;
    }
    if (startDate > endDate) {
      setError(t('reports.saveModal.errorDateOrder') || 'Start date must be on or before end date');
      return;
    }
    if (!selectedRestaurantId) {
      setError("Please select a restaurant");
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const payload = {
        name: name.trim(),
        startDate,
        endDate,
        sourcingData,
        restaurantId: selectedRestaurantId,
      };
      if (mode === 'create') {
          await createReport(payload);
      } else {
          await updateReport(reportId!, payload);
      }
      navigate('/reports-legacy');
    } catch (err: any) {
      console.error(err);
      setError(t('reports.saveModal.errorSave') || 'Could not save report');
      setSaving(false);
    }
  };

  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 dark:bg-black/50">
      <div className="bg-card dark:bg-card-dark rounded-xl w-full max-w-md p-6 relative border border-zinc-200 dark:border-white/20">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-muted dark:text-muted-dark hover:text-text dark:hover:text-text-dark"
          aria-label={t('reports.saveModal.cancel')}
        >
          <X className="w-5 h-5" />
        </button>

        <h2 className="text-xl font-semibold text-text dark:text-text-dark mb-4">
          {mode === 'create'
            ? t('reports.saveModal.title')
            : t('reports.saveModal.titleUpdate')}
        </h2>

        <label className="block mb-2 text-sm font-medium text-text dark:text-text-dark">
          {t('reports.saveModal.name')}
        </label>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="w-full p-2 border border-border dark:border-border-dark rounded-md bg-background dark:bg-background-dark text-text dark:text-text-dark mb-4"
        />

        {/* ─── Restaurant Dropdown ─── */}
        <label className="block mb-2 text-sm font-medium text-text dark:text-text-dark">
          {t("reports.saveModal.restaurant")}
        </label>
        <select
          value={selectedRestaurantId}
          onChange={(e) => setSelectedRestaurantId(e.target.value)}
          className="w-full p-2 border border-border dark:border-border-dark rounded-md bg-background dark:bg-background-dark text-text dark:text-text-dark mb-4"
        >
          <option value="">{t("reports.saveModal.selectRestaurant")}</option>
          {restaurants.map((r) => (
            <option key={r.id} value={r.id}>
              {r.name}
            </option>
          ))}
        </select>

        <div className="flex gap-4 mb-4">
          <div className="flex-1">
            <label className="block mb-2 text-sm font-medium text-text dark:text-text-dark">
              {t('reports.saveModal.startDate')}
            </label>
            <input
              type="date"
              value={startDate}
              max={endDate || today}
              onChange={(e) => setStartDate(e.target.value)}
              className="w-full p-2 border border-border dark:border-border-dark rounded-md bg-background dark:bg-background-dark text-text dark:text-text-dark"
            />
          </div>
          <div className="flex-1">
            <label className="block mb-2 text-sm font-medium text-text dark:text-text-dark">
              {t('reports.saveModal.endDate')}
            </label>
            <input
              type="date"
              value={endDate}
              min={startDate}
              max={today}
              onChange={(e) => setEndDate(e.target.value)}
              className="w-full p-2 border border-border dark:border-border-dark rounded-md bg-background dark:bg-background-dark text-text dark:text-text-dark"
            />
          </div>
        </div>

        {error && (
          <p className="text-sm text-destructive dark:text-destructive-dark mb-2">
            {error}
          </p>
        )}

        <div className="flex justify-between">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-muted dark:bg-muted-dark text-text dark:text-text-dark rounded-md hover:bg-muted/80 dark:hover:bg-muted-dark/80 transition"
            disabled={saving}
          >
            {t('reports.saveModal.cancel') || 'Cancel'}
          </button>
          <button
            onClick={handleSave}
            className="px-4 py-2 bg-accent dark:bg-accent-dark text-white dark:text-white rounded-md hover:bg-accent-dark dark:hover:bg-accent transition disabled:opacity-50"
            disabled={saving || !name.trim() || !startDate || !endDate || startDate > endDate}
          >
            {saving
              ? t('reports.saveModal.saving') || 'Saving…'
              : mode === 'create'
                ? t('reports.saveModal.saveReport')
                : t('reports.saveChanges')}
          </button>
        </div>
      </div>
    </div>
  );
}
